/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useQuery,
  UseQueryOptions,
  useMutation,
  useQueryClient,
  UseMutationOptions
} from '@tanstack/react-query';
import {apiClient} from '#lib/api-client';
import {queryKeys} from '#lib/query-client';
import {STALE_TIME, GC_TIME} from '#constants';
import {ENDPOINTS} from '@/src/utils/api';
import {useUserSettings} from './useSettings';

// NOTE: user/me endpoint should ONLY be used for DELETE (account deletion)
// For getting user profile info, use useUserSettings() instead

// Hook for getting user info - uses user/settings for members
// This returns name and avatar from user settings, not from user/me
export function useUserInfo(
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  // For members, use user settings endpoint instead of user/me
  const userSettingsQuery = useUserSettings(options);

  // Helper to create full name
  const getFullName = (
    first: string | null | undefined,
    last: string | null | undefined
  ) => {
    return `${first || ''} ${last || ''}`.trim() || null;
  };

  // Transform the settings response to match expected userInfo format
  return {
    ...userSettingsQuery,
    data: userSettingsQuery.data?.data
      ? {
          name: getFullName(
            userSettingsQuery.data.data.first_name,
            userSettingsQuery.data.data.last_name
          ),
          avatar: userSettingsQuery.data.data.avatar || null,
          // Include all other settings data
          ...userSettingsQuery.data.data
        }
      : userSettingsQuery.data
  };
}

// Deprecated: use useUserInfo() instead
// This hook is kept for backward compatibility but should not be used
export function useUserProfile(
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  console.warn(
    'useUserProfile is deprecated. Use useUserInfo() instead, which uses user/settings endpoint.'
  );
  return useUserInfo(options);
}

// Hook for getting user statistics
export function useUserStatistics(
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.user.statistics(),
    queryFn: () => apiClient.get(ENDPOINTS.user.statistic),
    staleTime: STALE_TIME.USER,
    gcTime: GC_TIME.USER,
    ...options
  });
}

// Hook for getting public webshop data - REMOVED
// This endpoint doesn't exist in the API
// Use organisation-specific endpoints instead

// Hook for getting walkthrough data
export function useWalkthrough(
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.user.walkthrough(),
    queryFn: () => apiClient.get(ENDPOINTS.user.walkthrought),
    staleTime: STALE_TIME.USER,
    gcTime: GC_TIME.USER,
    ...options
  });
}

// Hook for getting completed walkthrough step keys
export function useCompletedWalkthroughStepKeys(
  options?: Omit<UseQueryOptions<string[]>, 'queryKey' | 'queryFn'>
) {
  const walkthroughQuery = useWalkthrough(options);

  return {
    ...walkthroughQuery,
    data:
      walkthroughQuery.data?.data?.walkthrough_info
        ?.filter(({done}: {done: boolean}) => done)
        ?.map(({step_key}: {step_key: string}) => step_key) || []
  };
}

// Hook for skipping walkthrough step
export function useSkipWalkthroughStep(
  options?: UseMutationOptions<any, Error, {stepKey: string}>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({stepKey}: {stepKey: string}) =>
      apiClient.post(ENDPOINTS.user.skipWalkthrough, {stepKey}),
    onSuccess: () => {
      // Invalidate walkthrough query to refetch updated data
      queryClient.invalidateQueries({queryKey: queryKeys.user.walkthrough()});
    },
    ...options
  });
}
